import {
  ArrowLeft,
  ExternalLink,
  Save,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import styles from './WidgetHeader.module.css';
import { useAuthStore } from '../../../../store/authStore';
import { UserRole } from '../../../../enums/userRole';

/** Roles allowed to change the chat name. Explicit list - Admin and Workzone Manager are excluded. */
const CHAT_NAME_EDITOR_ROLES: number[] = [
  UserRole.SuperAdmin,
  UserRole.Admin,
  UserRole.WorkzoneManager,
];

interface WidgetHeaderProps {
  widgetName: string;
  widgetActive: boolean;
  isCreateMode: boolean;
  isSuperAdmin: boolean;
  canToggleActive: boolean;
  id?: string;
  dirty: boolean;
  saving: boolean;
  onWidgetNameChange: (name: string) => void;
  onToggleActive: () => void;
  onBack: () => void;
  onSave: () => void;
}

export default function WidgetHeader({
  widgetName,
  widgetActive,
  isCreateMode,
  canToggleActive,
  id,
  dirty,
  saving,
  onWidgetNameChange,
  onToggleActive,
  onBack,
  onSave,
}: WidgetHeaderProps) {
  const roleId = useAuthStore(state => state.user?.role?.id);
  const canEditName = roleId !== undefined && CHAT_NAME_EDITOR_ROLES.includes(roleId);

  return (
    <div className={styles.header}>
      <div className={styles.headerLeft}>
        <input
          type="text"
          className={styles.nameInput}
          value={widgetName}
          onChange={e => {
            if (canEditName) onWidgetNameChange(e.target.value);
          }}
          readOnly={!canEditName}
          style={{ width: Math.max(200, widgetName.length * 16) }}
        />
        {!isCreateMode && canToggleActive && (
          <button
            className={`${styles.statusBadge} ${widgetActive ? styles.statusActive : styles.statusInactive}`}
            onClick={onToggleActive}
            title={widgetActive ? 'Active - click to deactivate' : 'Inactive - click to activate'}
          >
            {widgetActive ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
            {widgetActive ? 'Active' : 'Inactive'}
          </button>
        )}
      </div>
      <div className={styles.headerRight}>
        {!isCreateMode && id && (
          <a
            className={styles.portalLink}
            href={`/portal/${id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLink size={14} /> Open Portal
          </a>
        )}
        <button className={styles.backBtn} onClick={onBack}>
          <ArrowLeft size={14} /> Back
        </button>
        <button
          className={styles.saveBtn}
          onClick={onSave}
          disabled={saving || (!dirty && !isCreateMode)}
        >
          <Save size={14} />
          {saving ? 'Saving...' : isCreateMode ? 'Create AI Agent' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
